<template>
  <div class="createpage">
    <image-selector
      v-if="model"
      class="view"
      v-model="image"
      :ratio="2.0 / 5.0"
      placeholder="/img/placeholders/worldview.png"
      @input="change"
    />
    <world-view-form :novel-id="novelId" v-model="model" />
    <button class="button" :disabled="!model.name || saving" @click="submit">{{ $t("common.create") }}</button>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import WorldViewForm from "@/components/organisms/WorldViewForm.vue";
import ImageSelector from "@/components/molecules/ImageSelector.vue";
import { submitImage } from "@/lib/image-save";
import { CreateWorldView, WorldView } from "@/lib/models";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { WorldViewForm, ImageSelector },
  props: {
    novelId: String,
    worldViewFolderId: String,
  },
  data() {
    return {
      model: {
        detail: [],
        additionalColumn: [],
      },
      image: "",
      saving: false,
      unbindShortcutKeys: null,
    };
  },
  created() {
    // 保存ボタンのショートカットキーの有効化と解除関数の取得
    this.unbindShortcutKeys = handleShortcutKeys([
      { trigger: TriggerKey.Meta, keys: ["s"], callback: this.submit },
      { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.submit },
    ]);
  },
  beforeDestroy() {
    // コンポーネントが破棄される前に保存ボタンのショートカットキーを解除
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();
  },
  methods: {
    change() {
      this.setModified(true);
    },
    async submit() {
      if (!this.model.name || this.saving) {
        return;
      }
      try {
        this.saving = true;

        const { novelId, worldViewFolderId, image } = this;
        const { additionalColumn } = this.model;
        this.model.additionalColumn = additionalColumn.filter((item) => item.label || item.content);

        const createWorldViewInput: CreateWorldView = {
          novelId,
          worldViewFolderId,
          image,
          ...this.model,
        };
        const worldView: WorldView = await this.$store.dispatch(
          "worldViewModule/createWorldView",
          createWorldViewInput
        );

        const { worldViewId } = worldView;

        await submitImage(`novels/${novelId}/worldviews/${worldViewId}.jpg`);

        this.$ga.event("novel", "create-worldview", "世界観新規作成", 0);
        this.setModified(false);
        this.$router.push({
          name: "worldViews",
          params: { novelId, worldViewId },
        });
      } finally {
        this.saving = false;

        this.$notify({
          title: this.$t("common.saved") as string,
          type: "success",
        });
      }
    },
  },
});

interface Props {
  novelId: string;
  worldViewFolderId: string;
}

interface Data {
  model: {
    detail: any[];
    additionalColumn: any[];
  };
  image: string;
  saving: boolean;
  unbindShortcutKeys: (() => void) | null;
}

interface Computed {}

interface Methods {
  change(): void;
  submit(): Promise<void>;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.createpage {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  padding: 0 12.5%;
  @include mq(sp) {
    padding: 0;
  }
}
.button {
  position: relative;
  margin: 0 auto;
  margin-bottom: 60px;
  padding: 15px 150px;
  border-radius: var(--radius-rounded);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border-color: var(--color-primary);

  @include hover-overlay;

  &:disabled {
    background-color: var(--color-surface-muted);
    color: var(--color-inactive);
    border: solid 1px var(--color-border);
    cursor: not-allowed;
    &::after {
      display: none;
    }
  }

  @include mq(sp) {
    margin: 15px 30px;
    margin-bottom: 50px;
    padding: 15px 30px;
    width: auto;
  }
}
</style>
